/**
 * Authorization-context service: talks to the gateway/BFF authz endpoints that back the
 * x-mia-acl-context / roles flow. Mirrors ai-foundry-website/src/services/{access,api}.ts.
 *
 * All calls go through getAuthorizedFetch() (which attaches Authorization: Bearer and,
 * via the fetchRegistry wrapper, the selected x-mia-acl-context). The per-organization
 * access probe passes its own x-mia-acl-context so it wins over the selected one.
 */

import config from '../config'
import { getAuthorizedFetch } from '../utils/fetchRegistry'
import { buildOrganizationAclContextHeaders } from '../utils/aclContext'

const bffBase = () => config.bffUrl || ''

const parseJson = async (response) => {
  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`)
    error.status = response.status
    throw error
  }
  if (response.status === 204 || response.status === 205) { return undefined }
  return response.json()
}

/**
 * Probe whether an organization has access to mia-flow via the gateway's /api/access
 * (OPA `mia_flow/access/allow`): 204 => has access, anything else / error => false.
 */
export const checkAccessForOrganization = async (organization, tenant) => {
  try {
    const response = await getAuthorizedFetch()('/api/access', {
      headers: buildOrganizationAclContextHeaders(organization, tenant),
    })
    return response.status === 204
  } catch {
    return false
  }
}

/** GET /api/bff/tenants — the tenant/organization list visible to the caller. */
export const listTenants = async () => {
  const response = await getAuthorizedFetch()(`${bffBase()}/tenants`, {
    headers: { Accept: 'application/json' },
  })
  const data = await parseJson(response)
  return Array.isArray(data) ? data : []
}

/** GET /api/bff/me/roles — the caller's mia-flow role URNs for the selected org/tenant. */
export const getCurrentUserRoles = async () => {
  const response = await getAuthorizedFetch()(`${bffBase()}/me/roles`, {
    headers: { Accept: 'application/json' },
  })
  const data = await parseJson(response)
  return data && Array.isArray(data.roles) ? data.roles : []
}

/** GET /api/bff/organization-configuration — per-organization product configuration. */
export const getOrganizationConfiguration = async () => {
  const response = await getAuthorizedFetch()(`${bffBase()}/organization-configuration`, {
    headers: { Accept: 'application/json' },
  })
  return parseJson(response)
}
